/**
 * Rate Limiting Service
 * Provides comprehensive protection against brute force attacks across all authentication flows
 */

interface RateLimitAttempt {
  count: number;
  lastAttempt: number;
  blocked: boolean;
  blockUntil?: number;
}

interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number;
  blockDurationMs: number;
  progressiveDelay?: boolean;
}

class RateLimitingService {
  private attempts: Map<string, RateLimitAttempt> = new Map();
  private readonly configs: Record<string, RateLimitConfig> = {
    login: {
      maxAttempts: 5,
      windowMs: 15 * 60 * 1000, // 15 minutes
      blockDurationMs: 30 * 60 * 1000, // 30 minutes
      progressiveDelay: true,
    },
    signup: {
      maxAttempts: 3,
      windowMs: 10 * 60 * 1000, // 10 minutes
      blockDurationMs: 60 * 60 * 1000, // 1 hour
      progressiveDelay: true,
    },
    inviteCode: {
      maxAttempts: 10,
      windowMs: 15 * 60 * 1000, // 15 minutes
      blockDurationMs: 30 * 60 * 1000, // 30 minutes
      progressiveDelay: false,
    },
    googleSignIn: {
      maxAttempts: 3,
      windowMs: 5 * 60 * 1000, // 5 minutes
      blockDurationMs: 15 * 60 * 1000, // 15 minutes
      progressiveDelay: false,
    },
    passwordReset: {
      maxAttempts: 3,
      windowMs: 60 * 60 * 1000, // 1 hour
      blockDurationMs: 24 * 60 * 60 * 1000, // 24 hours
      progressiveDelay: false,
    },
  };

  /**
   * Generate a unique identifier for rate limiting
   */
  private generateIdentifier(type: string, userIdentifier: string): string {
    // Combine type with user identifier (IP, email, etc.)
    return `${type}_${userIdentifier}`;
  }

  /**
   * Get device fingerprint for additional security
   */
  private getDeviceFingerprint(): string {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx?.fillText("ZuriLink", 0, 0);

    return btoa(
      JSON.stringify({
        userAgent: navigator.userAgent.slice(0, 50),
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screen: `${screen.width}x${screen.height}`,
        canvas: canvas.toDataURL().slice(0, 50),
        platform: navigator.platform,
      })
    );
  }

  /**
   * Check if an action is rate limited
   */
  public isRateLimited(
    type: string,
    userIdentifier: string
  ): {
    isLimited: boolean;
    remainingAttempts: number;
    resetTime: number;
    blockUntil?: number;
  } {
    const config = this.configs[type];
    if (!config) {
      throw new Error(`Unknown rate limit type: ${type}`);
    }

    const identifier = this.generateIdentifier(type, userIdentifier);
    const now = Date.now();
    const attempt = this.attempts.get(identifier);

    if (!attempt) {
      return {
        isLimited: false,
        remainingAttempts: config.maxAttempts,
        resetTime: now + config.windowMs,
      };
    }

    // Check if currently blocked
    if (attempt.blocked && attempt.blockUntil && now < attempt.blockUntil) {
      return {
        isLimited: true,
        remainingAttempts: 0,
        resetTime: attempt.blockUntil,
        blockUntil: attempt.blockUntil,
      };
    }

    // If block has expired, reset the attempt data
    if (attempt.blocked && attempt.blockUntil && now >= attempt.blockUntil) {
      this.attempts.set(identifier, {
        count: 0,
        lastAttempt: now,
        blocked: false,
      });
      this.persistToStorage(identifier, this.attempts.get(identifier)!);

      return {
        isLimited: false,
        remainingAttempts: config.maxAttempts,
        resetTime: now + config.windowMs,
      };
    }

    // Check if window has expired
    if (now - attempt.lastAttempt > config.windowMs) {
      // Reset the attempt counter
      this.attempts.set(identifier, {
        count: 0,
        lastAttempt: now,
        blocked: false,
      });

      return {
        isLimited: false,
        remainingAttempts: config.maxAttempts,
        resetTime: now + config.windowMs,
      };
    }

    // Check if over limit
    if (attempt.count >= config.maxAttempts) {
      const blockUntil = now + config.blockDurationMs;

      // Block the user
      this.attempts.set(identifier, {
        ...attempt,
        blocked: true,
        blockUntil,
      });

      return {
        isLimited: true,
        remainingAttempts: 0,
        resetTime: blockUntil,
        blockUntil,
      };
    }

    return {
      isLimited: false,
      remainingAttempts: config.maxAttempts - attempt.count,
      resetTime: attempt.lastAttempt + config.windowMs,
    };
  }

  /**
   * Record a failed attempt
   */
  public recordFailedAttempt(type: string, userIdentifier: string): void {
    const identifier = this.generateIdentifier(type, userIdentifier);
    const now = Date.now();
    const attempt = this.attempts.get(identifier);

    if (!attempt) {
      this.attempts.set(identifier, {
        count: 1,
        lastAttempt: now,
        blocked: false,
      });
    } else {
      this.attempts.set(identifier, {
        ...attempt,
        count: attempt.count + 1,
        lastAttempt: now,
      });
    }

    // Store in localStorage for persistence across sessions
    this.persistToStorage(identifier, this.attempts.get(identifier)!);
  }

  /**
   * Record a successful attempt (clears the rate limit)
   */
  public recordSuccessfulAttempt(type: string, userIdentifier: string): void {
    const identifier = this.generateIdentifier(type, userIdentifier);
    this.attempts.delete(identifier);
    this.removeFromStorage(identifier);
  }

  /**
   * Get progressive delay based on attempt count
   */
  public getProgressiveDelay(type: string, userIdentifier: string): number {
    const config = this.configs[type];
    if (!config.progressiveDelay) return 0;

    const identifier = this.generateIdentifier(type, userIdentifier);
    const attempt = this.attempts.get(identifier);

    if (!attempt) return 0;

    // Progressive delay: 1s, 2s, 4s, 8s, 16s...
    return Math.min(Math.pow(2, attempt.count) * 1000, 30000); // Max 30 seconds
  }

  /**
   * Persist rate limit data to localStorage
   */
  private persistToStorage(
    identifier: string,
    attempt: RateLimitAttempt
  ): void {
    try {
      const data = {
        ...attempt,
        deviceFingerprint: this.getDeviceFingerprint(),
      };
      localStorage.setItem(`rate_limit_${identifier}`, JSON.stringify(data));
    } catch (error) {
      // localStorage might be disabled - continue without persistence
    }
  }

  /**
   * Remove rate limit data from localStorage
   */
  private removeFromStorage(identifier: string): void {
    try {
      localStorage.removeItem(`rate_limit_${identifier}`);
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Load rate limit data from localStorage on initialization
   */
  private loadFromStorage(): void {
    try {
      const keys = Object.keys(localStorage).filter((key) =>
        key.startsWith("rate_limit_")
      );
      const now = Date.now();

      keys.forEach((key) => {
        try {
          const data = JSON.parse(localStorage.getItem(key) || "{}");
          const identifier = key.replace("rate_limit_", "");

          // Verify device fingerprint to prevent manipulation
          if (data.deviceFingerprint === this.getDeviceFingerprint()) {
            // Only load if not expired
            if (data.blockUntil && now < data.blockUntil) {
              this.attempts.set(identifier, data);
            } else if (
              data.lastAttempt &&
              now - data.lastAttempt < 24 * 60 * 60 * 1000
            ) {
              // Keep attempts for 24 hours max, but clear expired blocks
              const attemptData = {
                ...data,
                blocked: false, // Clear expired block status
                blockUntil: undefined, // Remove expired block timestamp
              };
              this.attempts.set(identifier, attemptData);
            } else {
              // Remove expired entries from localStorage
              localStorage.removeItem(key);
            }
          } else {
            // Remove entries with mismatched device fingerprint
            localStorage.removeItem(key);
          }
        } catch (error) {
          // Remove corrupted data
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      // localStorage not available
    }
  }

  /**
   * Clean up expired entries
   */
  private cleanup(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    this.attempts.forEach((attempt, identifier) => {
      // Remove entries older than 24 hours
      if (now - attempt.lastAttempt > 24 * 60 * 60 * 1000) {
        expiredKeys.push(identifier);
      }
    });

    expiredKeys.forEach((key) => {
      this.attempts.delete(key);
      this.removeFromStorage(key);
    });
  }

  /**
   * Get rate limit status for display
   */
  public getRateLimitStatus(
    type: string,
    userIdentifier: string
  ): {
    isLimited: boolean;
    message: string;
    remainingTime?: number;
  } {
    const status = this.isRateLimited(type, userIdentifier);

    if (!status.isLimited) {
      return {
        isLimited: false,
        message: `${status.remainingAttempts} attempts remaining`,
      };
    }

    const remainingTime = status.blockUntil
      ? status.blockUntil - Date.now()
      : 0;
    const minutes = Math.ceil(remainingTime / 60000);

    return {
      isLimited: true,
      message: `Too many attempts. Please try again in ${minutes} minute${
        minutes !== 1 ? "s" : ""
      }.`,
      remainingTime,
    };
  }

  /**
   * Initialize the service
   */
  public initialize(): void {
    this.loadFromStorage();

    // Clean up expired entries every 5 minutes
    setInterval(() => this.cleanup(), 5 * 60 * 1000);
  }
}

// Export singleton instance
export const rateLimitingService = new RateLimitingService();

// Initialize on import
rateLimitingService.initialize();

// Export types for use in components
export type { RateLimitConfig };
